import { ChatText, Plus } from "@phosphor-icons/react";
import { apiClient, type PaginatedResponse } from "@/shared/lib/api/client";
import type { FeaturePalette, Result, ScopeNode, SearchProvider } from "@/shared/components/command-palette/types";
import type { Conversation } from "./types";

const CHAT_PARENT: ScopeNode = { id: "chat", parentId: null, title: "Chat" };

async function fetchConversations(): Promise<Conversation[]> {
  const res = await apiClient<PaginatedResponse<Conversation>>(
    "/api/chat/conversations?skip=0&limit=50&include_archived=false"
  );
  return res.items;
}

const conversationsProvider: SearchProvider = {
  id: "conversations",
  label: "Conversations",
  icon: ChatText,
  prefix: "@chat",
  async search(q, signal) {
    if (!q) return [];
    try {
      const items = await fetchConversations();
      if (signal.aborted) return [];
      const lower = q.toLowerCase();
      return items
        .filter((c) => (c.title ?? "").toLowerCase().includes(lower))
        .slice(0, 6)
        .map<Result>((c) => ({
          id: `conv:${c.id}`,
          title: c.title || "Untitled conversation",
          subtitle: c.turn_count > 0 ? `${c.turn_count} turns` : "Empty",
          icon: ChatText,
          scopeChain: [CHAT_PARENT],
          score: 40,
          onSelect: (ctx) => {
            ctx.router.push(`/chat/${c.id}`);
            ctx.close();
          },
        }));
    } catch {
      return [];
    }
  },
};

export const chatPalette: FeaturePalette = {
  commands: [
    {
      id: "chat.new",
      title: "New conversation",
      subtitle: "Start a fresh chat with the agent",
      icon: Plus,
      scopeId: "chat",
      keywords: [
        "new", "create", "start", "chat", "conversation", "thread",
        "agent", "ai", "assistant", "ask", "talk", "message",
      ],
      run: (ctx) => ctx.router.push("/chat"),
    },
    {
      id: "chat.open-library",
      title: "Open conversations",
      subtitle: "Browse your past chats",
      icon: ChatText,
      scopeId: "chat",
      keywords: ["open", "view", "list", "browse", "library", "conversations", "history"],
      run: (ctx) => ctx.router.push("/chat"),
    },
  ],
  searchProviders: [conversationsProvider],
};
